import {
  Component,
  Inject,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';
import { NetworkedUsersSelectorComponent } from '../../shared/networked-users-selector/networked-users-selector.component';
import { NetworkedEmployeesSelectorComponent } from '../../shared/networked-employees-selector/networked-employees-selector.component';
import { UtilsService } from '../../shared/utils.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SitesSelectorComponent } from '../../shared/sites-selector/sites-selector.component';
import { RBACService } from '../../shared/rbac.service';

@Component({
  selector: 'app-sites-toolbox-talks-filter',
  templateUrl: './sites-toolbox-talks-filter.component.html',
  styleUrls: ['./sites-toolbox-talks-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SitesToolboxTalksFilterComponent implements OnInit {
  // Define the default filters object.
  filters: {
    reported_by_ids: number[];
    employee_ids: number[];
    attendee_ids: number[];
    date_range: Date[];
    site_ids: number[];
    parent_site_id: number;
  } = {
    reported_by_ids: [],
    employee_ids: [],
    attendee_ids: [],
    date_range: [],
    site_ids: [],
    parent_site_id: null
  };

  // RBAC permission flags. Resolved once in ngOnInit() to avoid re-evaluating on every change detection cycle.
  canViewEmployees: boolean = false;

  constructor(
    public utils: UtilsService,
    public rbac: RBACService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<SitesToolboxTalksFilterComponent>
  ) {}

  ngOnInit() {
    // Resolve RBAC permission flags.
    this.canViewEmployees = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_EMPLOYEES,
          RBACService.RBAC_A.A_VIEW
        )
      ],
      true
    );

    this.filters.reported_by_ids = this.data['reported_by_ids'] || [];
    this.filters.employee_ids = this.data['employee_ids'] || [];
    this.filters.attendee_ids = this.data['attendee_ids'] || [];
    this.filters.date_range = this.data['date_range'] || [];
    this.filters.site_ids = this.data['site_ids'] || [];
    this.filters.parent_site_id = this.data['parent_site_id'] || null;
  }

  getOutputDateRange($event) {
    this.filters.date_range = $event;
  }

  onSelectReportedBy() {
    this.utils.showComponentDialog(
      NetworkedUsersSelectorComponent,
      {
        selected: this.filters.reported_by_ids,
        visitors_from_all_sites: true
      },
      {
        width: '1200px'
      },
      (results: number[]) => {
        if (!results) {
          return;
        }
        this.filters.reported_by_ids = results;
      }
    );
  }

  onSelectEmployees() {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_EMPLOYEES,
            RBACService.RBAC_A.A_VIEW
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to view employees.'
      );
      return;
    }
    this.utils.showComponentDialog(
      NetworkedEmployeesSelectorComponent,
      {
        selected: this.filters.employee_ids,
        multiple: true
      },
      {
        width: '1200px'
      },
      (results: number[]) => {
        if (!results) {
          return;
        }
        this.filters.employee_ids = results;
      }
    );
  }

  onSelectAttendees() {
    this.utils.showComponentDialog(
      NetworkedUsersSelectorComponent,
      {
        selected: this.filters.attendee_ids,
        multiple: true,
        visitors_from_all_sites: true
      },
      {
        width: '1200px'
      },
      (results: number[]) => {
        if (!results) {
          return;
        }
        this.filters.attendee_ids = results;
      }
    );
  }

  /**
   * Open the site selector.
   */
  onSelectSites() {
    this.utils
      .showComponentDialog(
        SitesSelectorComponent,
        {
          parent_site_id: this.filters.parent_site_id,
          selected: this.filters.site_ids,
          multiple: true
        },
        {
          width: '1200px'
        }
      )
      .then((results) => {
        if (typeof results !== 'undefined') {
          this.filters.site_ids = results;
        }
      });
  }

  onClearAndClose() {
    // Clear filters.
    this.filters.reported_by_ids = [];
    this.filters.employee_ids = [];
    this.filters.attendee_ids = [];
    this.filters.date_range = [];
    this.filters.site_ids = [];

    this.onSaveAndClose();
  }

  onSaveAndClose() {
    this.dialogRef.close(this.filters);
  }

  /**
   * Clears the date range for this filter.
   *
   * @returns {void}
   */
  onClearDateRange(): void {
    this.filters.date_range = [];
  }

  onClose() {
    this.dialogRef.close();
  }

  // Expose RBACService to template.
  protected readonly RBACService = RBACService;
}
